import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { Slot } from "radix-ui"

import { cn } from "~/lib/utils"

const badgeVariants = cva(
  "inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded-full border border-transparent px-2 h-[20px] text-[11px] font-medium tracking-[0.01em] whitespace-nowrap transition-colors focus-visible:ring-2 focus-visible:ring-ring/40 [&>svg]:pointer-events-none [&>svg]:size-3",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground [a&]:hover:bg-primary/90",
        secondary:
          "bg-muted text-foreground [a&]:hover:bg-accent",
        destructive:
          "bg-destructive/12 text-destructive [a&]:hover:bg-destructive/18",
        success:
          "bg-success/15 text-[oklch(0.40_0.13_145)] dark:text-[oklch(0.85_0.15_145)] [a&]:hover:bg-success/22",
        warning:
          "bg-warning/18 text-[oklch(0.45_0.13_70)] dark:text-[oklch(0.88_0.13_70)] [a&]:hover:bg-warning/25",
        outline:
          "border-hairline text-foreground [a&]:hover:bg-accent",
        ghost: "[a&]:hover:bg-accent [a&]:hover:text-accent-foreground",
        link: "text-foreground underline-offset-4 [a&]:hover:underline",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

function Badge({
  className,
  variant = "default",
  asChild = false,
  ...props
}: React.ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot.Root : "span"

  return (
    <Comp
      data-slot="badge"
      data-variant={variant}
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    />
  )
}

export { Badge, badgeVariants }
